import * as React from "react";

import * as DV from "@impero-com/common/utils/dataView";
import { assertNever } from "@impero-com/common/utils/obj";

import { MetadataInstanceValue } from "api/generated/model";
import * as drawerTheme from "components/drawer/theme.css";
import AttachedFilesMetadataField, {
    UpdateRootNodeLinkedResourcesCallback,
} from "org/risks/widgets/attachedFilesField";
import { CheckboxGroupMetadataField } from "org/risks/widgets/checkboxGroupMetadataField";
import DropdownMetadataField from "org/risks/widgets/dropdownMetadataField";
import HeadingMetadataField from "org/risks/widgets/headingMetadataField";
import { MetadataErrorWidget } from "org/risks/widgets/metadataError";
import { NumericInputMetadataField } from "org/risks/widgets/numericInputMetadataField";
import RadioMetadataField from "org/risks/widgets/radioMetadataField";
import TextAreaMetadataField from "org/risks/widgets/textAreaMetadataField";
import TextInputMetadataField from "org/risks/widgets/textInputMetadataField";
import * as WT from "org/risks/widgets/types";
import {
    CustomizedValidation,
    FormComponentProps,
    subForm,
    ValidationRule,
} from "utils/form";
import { Guid } from "utils/guid";

import * as T from "../types";
import * as U from "../utils";

import * as theme from "./theme.css";

// Cannot be pure because renderItem may return different content
class SectionsLayout<T> extends React.Component<SectionsLayoutProps<T>> {
    render = () => {
        const { sections, renderItem } = this.props;
        return sections.map((section) => (
            <div key={section.id} className={theme.section}>
                {(() => {
                    const result: Array<React.ReactNode> = [];
                    section.sectionColumns.forEach((column, columnIndex) => {
                        if (columnIndex > 0) {
                            result.push(
                                <div
                                    key={`sep-${columnIndex}`}
                                    className={theme.sectionColumnSeparator}
                                />
                            );
                        }
                        result.push(
                            <div
                                key={columnIndex}
                                className={theme.sectionColumn}
                            >
                                {column.map((item, itemIndex) => (
                                    <div
                                        key={itemIndex}
                                        className={theme.sectionItemSpacer}
                                    >
                                        {renderItem(
                                            item,
                                            section.sectionColumns.length <= 1
                                                ? 2
                                                : 1
                                        )}
                                    </div>
                                ))}
                            </div>
                        );
                    });
                    return result;
                })()}
            </div>
        ));
    };
}

interface LayoutSection<T> {
    id: Guid;
    sectionColumns: Array<Array<T>>;
}

interface SectionsLayoutProps<T> {
    sections: Array<LayoutSection<T>>;
    renderItem: (item: T, columnWidth: number) => React.ReactNode;
}

class MetadataFieldSectionsLayout<
    ID extends string,
    ROOT,
> extends React.PureComponent<MetadataFieldSectionsLayoutProps<ID, ROOT>> {
    render = () => {
        const { sections } = this.props;
        // FIXME lambda to force update
        return (
            <SectionsLayout sections={sections} renderItem={this.renderField} />
        );
    };

    renderField = (
        extractor: DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>,
        columnWidth: number
    ) => {
        const { node, type, specialProperties } = this.props;

        let customizedValidations: CustomizedValidations = {};
        if (type === "EDIT" && this.props.customizedValidations) {
            customizedValidations = this.props.customizedValidations;
        }

        let shared: WT.BaseSharedProperties<string, ROOT> & { key: string } & {
            validators?: ValidationRule<string | undefined>[];
        };

        const untypedInstance = extractor.getValue(node);
        if (untypedInstance === undefined) {
            throw new Error("Could not find instance");
        }
        const special =
            specialProperties !== undefined
                ? specialProperties(extractor, untypedInstance, type)
                : undefined;
        const effectiveType =
            special?.type ||
            (untypedInstance.type === "MetadataError" ||
            U.hasFormula(untypedInstance.fieldType)
                ? "READ_ONLY"
                : undefined) ||
            type;

        switch (effectiveType) {
            case "READ_ONLY": {
                const { type: _type, ...override } = special ?? {};
                shared = {
                    key: extractor.id,
                    id: extractor.id,
                    label: extractor.label,
                    type: "READ_ONLY",
                    ...override,
                };
                break;
            }
            case "EDIT": {
                const { type: _type, ...override } = special ?? {};
                const validationError = customizedValidations[extractor.id];
                shared = {
                    key: extractor.id,
                    id: extractor.id,
                    label: extractor.label,
                    type: "EDIT",
                    onChangeAsync: async <PV,>(
                        promiseValue: Promise<PV>,
                        afterCallback: (
                            prevState: MetadataInstanceValue | undefined,
                            promisedValue: PV
                        ) => MetadataInstanceValue | null
                    ) => {
                        if (this.props.type !== "EDIT") {
                            throw new Error("Not an EDIT widget");
                        }
                        return this.props.onChangeFieldValueAsync(
                            extractor.id,
                            promiseValue,
                            afterCallback
                        );
                    },
                    validationError,
                    ...override,
                };
                break;
            }
            case "CREATE":
                const { type: _type, ...override } = special ?? {};
                if (this.props.type !== "CREATE") {
                    throw new Error("Invalid effectiveType");
                }
                const { id, form } = this.props;

                shared = {
                    key: extractor.id,
                    id: extractor.id,
                    label: extractor.label,
                    type: "CREATE",
                    form: subForm(id, form),
                    ...override,
                };
                break;
            default:
                return assertNever(effectiveType);
        }
        switch (untypedInstance.type) {
            case "TextAreaInstance": {
                const instance = untypedInstance;
                return (
                    <TextAreaMetadataField {...shared} instance={instance} />
                );
            }
            case "NumericInputInstance": {
                const instance = untypedInstance;
                return (
                    <NumericInputMetadataField
                        {...shared}
                        instance={instance}
                    />
                );
            }
            case "DropdownInstance": {
                const instance = untypedInstance;
                return (
                    <DropdownMetadataField
                        {...shared}
                        instance={instance}
                        getPopupContainer={(el) =>
                            el.closest(`.${drawerTheme.content}`) ??
                            (undefined as any)
                        }
                    />
                );
            }
            case "CheckboxGroupInstance": {
                const instance = untypedInstance;
                return (
                    <CheckboxGroupMetadataField
                        {...shared}
                        instance={instance}
                        columns={columnWidth}
                    />
                );
            }
            case "TextInputInstance": {
                const instance = untypedInstance;
                return (
                    <TextInputMetadataField {...shared} instance={instance} />
                );
            }
            case "RadioButtonGroupInstance": {
                const instance = untypedInstance;
                return (
                    <RadioMetadataField
                        {...shared}
                        instance={instance}
                        columns={columnWidth}
                    />
                );
            }
            case "HeadingInstance": {
                return (
                    <HeadingMetadataField
                        key={extractor.id}
                        id={extractor.id}
                        name={extractor.label}
                    />
                );
            }
            case "MetadataError": {
                const error = untypedInstance;
                return (
                    <MetadataErrorWidget
                        key={extractor.id}
                        id={extractor.id}
                        label={extractor.label}
                        error={error}
                    />
                );
            }
            case "AttachedFilesInstance": {
                const instance = untypedInstance;
                return (
                    <AttachedFilesMetadataField
                        {...shared}
                        instance={instance}
                        updateRootNodeLinkedResources={
                            this.props.updateRootNodeLinkedResources
                        }
                    />
                );
            }
            default:
                return assertNever(untypedInstance);
        }
    };
}

interface MetadataFieldSectionsLayoutCommonProps {
    sections: Array<
        LayoutSection<DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>>
    >;
    extractors: Array<DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>>;
    node: T.NodeWithMD;
    /**
     *  Allow special props for a given field, be aware that type can only be reduce from "EDIT" | "CREATE" to "READ_ONLY"
     */
    specialProperties?: (
        extractor: DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>,
        instance: T.MetadataInstance,
        type: "READ_ONLY" | "EDIT" | "CREATE"
    ) => PartialSharedProperties | undefined;
    updateRootNodeLinkedResources: UpdateRootNodeLinkedResourcesCallback;
}

type MetadataFieldSectionsLayoutProps<ID extends string, ROOT> =
    | ({ type: "READ_ONLY" } & MetadataFieldSectionsLayoutCommonProps)
    | ({ type: "EDIT" } & {
          onChangeFieldValueAsync: <PV>(
              fieldId: Guid,
              promiseValue: Promise<PV>,
              callback: (
                  prevState: MetadataInstanceValue | undefined,
                  promisedValue: PV
              ) => MetadataInstanceValue | null
          ) => Promise<void>;
          customizedValidations?: CustomizedValidations;
      } & MetadataFieldSectionsLayoutCommonProps)
    | ({ type: "CREATE" } & {
          id: ID;
      } & MetadataFieldSectionsLayoutCommonProps &
          FormComponentProps<ID, Record<Guid, T.MetadataInstance>, ROOT>);

interface BaseProperties<CID extends string> {
    key: string;
    id: CID;
    label: string;
}

interface ReadOnlyProperties<CID extends string> extends BaseProperties<CID> {
    type: "READ_ONLY";
}

interface EditProperties<CID extends string> extends BaseProperties<CID> {
    onChange: (value: T.MetadataInstance | undefined) => void;
}

interface CreateProperties<CID extends string, ROOT>
    extends
        BaseProperties<CID>,
        FormComponentProps<CID, T.MetadataInstance, ROOT> {
    type: "CREATE";
}

type SharedProperties<CID extends string, ROOT> =
    | ReadOnlyProperties<CID>
    | EditProperties<CID>
    | CreateProperties<CID, ROOT>;
//type PartialSharedProperties<CID extends string> = { type?: "READ_ONLY" } & Partial<ReadOnlyProperties<CID>>;
interface PartialSharedProperties {
    type?: "READ_ONLY";
}

interface CustomizedValidations {
    /// key: FieldID
    [key: string]: CustomizedValidation;
}

export type {
    CustomizedValidations,
    MetadataFieldSectionsLayoutProps,
    ReadOnlyProperties,
    EditProperties,
    SharedProperties,
    PartialSharedProperties,
};
export { MetadataFieldSectionsLayout };
